import type {
  IncentiveProgramEntity,
  IncentiveProgramsResponseEntity,
} from "@features/dynamic-incentives/domain";
import { LEVELS } from "../constants/levels";
import type {
  IncentiveProgramModel,
  NewIncentiveProgramModel,
  PreviousIncentiveSummaryModel,
} from "../models";

/**
 * Map incentive program model to entity
 */
export function mapIncentiveProgram(
  model: IncentiveProgramModel,
): IncentiveProgramsResponseEntity {
  return {
    currentIncentive: model.currentIncentive
      ? mapCurrentIncentiveProgram(model.currentIncentive)
      : undefined,
    prevIncentive: model.prevIncentiveSummary
      ? mapPreviousIncentiveSummary(model.prevIncentiveSummary)
      : undefined,
  };
}

/**
 * Map new incentive program model to entity
 */
function mapCurrentIncentiveProgram(
  model: NewIncentiveProgramModel,
): IncentiveProgramEntity {
  const isActive =
    new Date(model.startDate) < new Date() &&
    new Date(model.endDate) > new Date();

  return {
    levels: LEVELS,
    isAchieved: model.expectedRewards > 0,
    isOver: new Date(model.endDate) < new Date(),
    isStarted: new Date(model.startDate) < new Date(),
    isActive,
    isEligible:
      model.isEligible && model.deliveredOrders >= LEVELS[0].minOrders,
    startDate: new Date(model.startDate),
    endDate: new Date(model.endDate),

    /** In the UI, user should always be at a minimum of level 1.
     * Level 0 indicates that the user is not yet eligible for the program
     * (needs to reach minimum delivery rate)
     */
    currentLevel: LEVELS.find(level => level.level === model.currentLevel || 1),
    nextLevel: LEVELS.find(
      level => level.level === (model.currentLevel || 1) + 1,
    ),
    deliveredOrders: model.deliveredOrders,
    minimumDeliveryRate: model.minimumDeliveryRate,
    rewards: model.reward || model.expectedRewards,
    deliveryRate: model.deliveryRate,
    totalOrders: model.totalOrdersCount,
    isLastLevel: model.currentLevel === LEVELS.length,
    deliveryCalculationDate: new Date(model.deliveryCalculationDate),
    isOnGracePeriod:
      !isActive && new Date(model.deliveryCalculationDate) > new Date(),
  };
}

/**
 * Map previous incentive summary model to entity
 */
function mapPreviousIncentiveSummary(
  model: PreviousIncentiveSummaryModel,
): IncentiveProgramEntity {
  return {
    deliveryRate: model.deliveryRate,
    startDate: new Date(model.startDate),
    endDate: new Date(model.endDate),
    levels: LEVELS,
    rewards: model.totalRewards,
    minimumDeliveryRate: model.minimumDeliveryRate,
    deliveredOrders: (model.deliveryRate / 100) * model.totalOrdersCount,
    totalOrders: model.totalOrdersCount,
    isAchieved: Boolean(model.totalRewards),
    isOver: true,
    isEligible: false,
    isStarted: true,
    isActive: false,
    isLastLevel: false,
    isPreviousProgram: true,
    deliveryCalculationDate: new Date(model.deliveryCalculationDate),
    isOnGracePeriod: new Date(model.deliveryCalculationDate) > new Date(),
  };
}
